/**
 * Template-labels API client — the FTL template layer's presentation SKIN.
 *
 * The backend template layer (Slice 1: `guardrail_resolver.py` +
 * `template_manifests`; Slice 2: the built-in Marketing manifest) resolves a
 * per-project-type MANIFEST that RELABELS the FIXED canonical entity types for
 * presentation without ever adding, removing, or re-typing a determination
 * type (the canonical 5 are patent-frozen). This endpoint exposes just the
 * `labels` overlay to the frontend so a project can render vocabulary that
 * matches its domain (e.g. a marketing campaign shows "Campaign Goal" where a
 * software project shows "Objective").
 *
 * Contract: `GET /projects/{id}/template-labels` → `{ project_type, labels }`.
 *   - `project_type` — the project's resolved type ("general" for the vast
 *     majority; "marketing_campaign" once Slice 3 flips a project to marketing).
 *   - `labels` — a `Record<canonicalType, displayLabel>` overlay. EMPTY `{}`
 *     for any project whose type has no built-in manifest (i.e. every existing
 *     software/general project today), which is the zero-visible-change safety
 *     contract: an empty overlay falls back to the canonical labels.
 *
 * The manifest `labels` keys are canonical entity-type slugs
 * (objective / module / user_persona / document / test_case …); each value is a
 * `{singular, plural}` display pair (the backend `guardrail_resolver.labels()`
 * normalizes every stored shape to this pair, so the wire is always the object
 * form — e.g. `{"singular":"Campaign Goal","plural":"Campaign Goals"}`). Only
 * the types the manifest chose to relabel appear — an absent key means "use the
 * canonical label" (handled by the consumer's humanized fallback).
 */

import { apiClient } from "@/api/client";

/** A single relabel entry — the authored singular + plural display forms. */
export interface TemplateLabelPair {
  singular: string;
  plural: string;
}

/** The `labels` overlay: canonical entity-type slug → `{singular, plural}`. */
export type TemplateLabelMap = Record<string, TemplateLabelPair>;

/** Response of `GET /projects/{id}/template-labels`. */
export interface TemplateLabelsResponse {
  /** Resolved project type — "general" (no overlay) or a manifest type. */
  project_type: string;
  /** Relabel overlay; `{}` when no manifest applies (zero visible change). */
  labels: TemplateLabelMap;
  /** Project route slugs this type does not use (row 4aa3e0ec); `[]` or
   *  absent for software/general projects, which keep every page. */
  hidden_surfaces?: string[];
}

/**
 * Fetch the presentation label overlay for a project. Returns the (possibly
 * empty) `labels` map + the resolved `project_type`. An empty map is the
 * expected, safe result for every non-marketing project.
 */
export function getTemplateLabels(
  projectId: string,
): Promise<TemplateLabelsResponse> {
  return apiClient.get<TemplateLabelsResponse>(
    `/projects/${projectId}/template-labels`,
  );
}

/** A single available project-type template in the create-project catalog. */
export interface TemplateCatalogEntry {
  /** The `project_type` value a new project must be created with to get this skin. */
  project_type: string;
  /** Human template name from the manifest (e.g. "Marketing Campaign"), or null. */
  template_name: string | null;
  /** The relabel overlay this template applies — for previewing its vocabulary. */
  labels: TemplateLabelMap;
}

/** Response of `GET /projects/template-catalog`. */
export interface TemplateCatalogResponse {
  templates: TemplateCatalogEntry[];
}

/**
 * Fetch the catalog of available project-type templates (built-in manifests
 * visible to the org + any the org authored). Powers the create-project picker:
 * which types carry a specialized vocabulary, and a preview of the relabels.
 * A `project_type` with no manifest simply never appears here.
 */
export function getTemplateCatalog(): Promise<TemplateCatalogResponse> {
  return apiClient.get<TemplateCatalogResponse>(`/projects/template-catalog`);
}

export const templateLabelsApi = { getTemplateLabels, getTemplateCatalog };

export default templateLabelsApi;
